import _, {noop} from 'lodash';
import {DateTime} from 'luxon';
import {ellipsis} from 'polished';
import React, {FC, ReactElement, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemSeparator} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemSeparator';
import {DateFormat} from '../../../../../libs/shared/ui/src/components/internationalization/dateFormat';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {
  buildRecurrenceTextForFrontRecurrence,
  LuxonWeekdayToCalendarWeekDays,
} from '../../../../helpers/models/calendarEventHelpers';
import {
  buildRecurrence,
  getWeekOfMonth,
  RecurrenceWeekOfMonthToText,
} from '../../../../helpers/models/recurringEventHelpers';
import {CalendarWeekDaysEnum} from '../../../../models/api/calendarCommonTypes';
import {RecurrenceFrequenciesEnum, RecurrenceModel} from '../../../../models/api/recurrenceModel';
import {CustomRecurrenceDialog} from './customRecurrenceDialog';

/*
 * Props.
 */

interface RecurrenceDropdownBoxProps {
  /* If the event being edited is an exception event, this is set to the recurrence of the master */
  masterRecurrence?: Readonly<RecurrenceModel>;
  /* The recurrence of the event being edited */
  recurrence?: Readonly<RecurrenceModel>;
  /* Teammate's calendar weekstart preference */
  weekStart: CalendarWeekDaysEnum;
  /* The start of the event we're setting a recurrence for. */
  startDateTime: DateTime;
  /* Change handler for recurrence. If none is specified, this becomes read-only. */
  onChange?: (arg: RecurrenceModel | undefined) => void;
}

/*
 * Styles.
 */

const StyledRecurrenceDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0px;
`;

const StyledValueDiv = styled.div`
  display: grid;
`;

const StyledRecurrenceTextDiv = styled.div`
  ${ellipsis()};
`;

/*
 * Component.
 */

export const RecurrenceDropdownBox: FC<RecurrenceDropdownBoxProps> = (props) => {
  const [shouldShowCustomRecurrenceDialog, setCustomRecurrenceDialog] = useState(false);
  const recurrenceOptions = useMemo(() => createRecurrenceOptions(props), [props]);
  const selectedOption = recurrenceOptions.find((option) => option.isSelected);

  return (
    <>
      <StyledRecurrenceDropdownBoxCoordinator
        isDisabled={!props.onChange}
        value={renderValue(props, selectedOption)}
        renderDropdown={(popoverProps) =>
          renderDropdown(popoverProps, recurrenceOptions, setCustomRecurrenceDialog, props)
        }
      />
      {shouldShowCustomRecurrenceDialog &&
        renderCustomRecurrenceDialog(setCustomRecurrenceDialog, props, selectedOption)}
    </>
  );
};

const doesNotRepeatText = <Trans id="recurrence-does-not-repeat">Does not repeat</Trans>;

function renderValue(props: RecurrenceDropdownBoxProps, selectedRecurrenceOption?: RecurrenceOption) {
  const {masterRecurrence, onChange} = props;
  const isReadOnly = !onChange;

  let text;
  if (isReadOnly && masterRecurrence) {
    // If the dropdown is read-only and this event is a series exception,
    // display the recurrence of its series master.
    text = buildRecurrenceTextForFrontRecurrence(masterRecurrence);
  } else {
    text = selectedRecurrenceOption ? selectedRecurrenceOption.text : doesNotRepeatText;
  }

  return (
    <StyledValueDiv>
      <StyledRecurrenceTextDiv>{text}</StyledRecurrenceTextDiv>
    </StyledValueDiv>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  recurrenceOptions: Array<RecurrenceOption>,
  setCustomRecurrenceDialog: (val: boolean) => void,
  props: RecurrenceDropdownBoxProps,
) {
  const onChange = props.onChange || noop;
  const recurrenceDoesNotExist = recurrenceOptions.every((option) => !option.isSelected);

  return (
    <Dropdown {...popoverProps}>
      <DropdownList {...popoverProps}>
        {recurrenceOptions.map((recurrenceOption) => renderItem(recurrenceOption, onChange))}

        <DropdownItem
          key="does-not-repeat"
          isChecked={recurrenceDoesNotExist}
          onSelect={() => onChange(undefined)}
        >
          <DropdownItemContent isChecked={recurrenceDoesNotExist}>{doesNotRepeatText}</DropdownItemContent>
        </DropdownItem>
        <DropdownItemSeparator />
        <DropdownItem
          key="custom-recurrence"
          isChecked={false}
          onSelect={() => setCustomRecurrenceDialog(true)}
        >
          <DropdownItemContent isChecked={false}>
            <Trans id="custom-recurrence">Custom</Trans>
          </DropdownItemContent>
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );
}

function renderItem(
  recurrenceOption: RecurrenceOption,
  onChange: (arg: RecurrenceModel | undefined) => void,
) {
  return (
    <DropdownItem
      key={recurrenceOption.key}
      isChecked={recurrenceOption.isSelected}
      onSelect={() => onChange(recurrenceOption.recurrence)}
    >
      <DropdownItemContent isChecked={recurrenceOption.isSelected}>
        {recurrenceOption.text}
      </DropdownItemContent>
    </DropdownItem>
  );
}

type RecurrenceOption = {
  recurrence: RecurrenceModel;
  key: string;
  text: ReactElement;
  isSelected: boolean;
};

const unsupportedRecurrenceText = <Trans id="recurrence-option-custom">Custom recurrence</Trans>;

function createRecurrenceOptions(props: RecurrenceDropdownBoxProps) {
  const {recurrence} = props;
  const defaultOptions = createDefaultRecurrenceOptions(props);

  if (!recurrence) {
    return defaultOptions;
  }

  // If there's an existing recurrence that matches a default RecurrenceOption,
  // set that option's isSelected property to true.
  const defaultRecurrenceOptionMatch = defaultOptions.find((option) =>
    _.isEqual(recurrence, option.recurrence),
  );
  if (defaultRecurrenceOptionMatch) {
    return defaultOptions.map((option) =>
      option === defaultRecurrenceOptionMatch ? {...option, isSelected: true} : option,
    );
  }

  // If the existing recurrence doesn't match any of the default options,
  // create an associated selected recurrence option.
  const customRecurrence = {
    recurrence,
    key: 'recurrence-option-custom',
    text: <>{buildRecurrenceTextForFrontRecurrence(recurrence) || unsupportedRecurrenceText}</>,
    isSelected: true,
  };

  return [...defaultOptions, customRecurrence];
}

function createDefaultRecurrenceOptions(props: RecurrenceDropdownBoxProps): Array<RecurrenceOption> {
  const {startDateTime, weekStart} = props;
  const {weekday} = startDateTime;
  const jsDate = startDateTime;

  const weekOfMonth = getWeekOfMonth(startDateTime);
  const weekOfMonthLong = RecurrenceWeekOfMonthToText[weekOfMonth];
  const frontCalendarWeekday = LuxonWeekdayToCalendarWeekDays[weekday];

  return [
    {
      recurrence: buildRecurrence({freq: RecurrenceFrequenciesEnum.DAILY}),
      key: 'recurrence-option-daily',
      text: <Trans id="recurrence-option-daily">Daily</Trans>,
      isSelected: false,
    },
    {
      recurrence: buildRecurrence({
        freq: RecurrenceFrequenciesEnum.WEEKLY,
        byweekday: [{weekday: frontCalendarWeekday, n: undefined}],
        wkst: weekStart,
      }),
      key: 'recurrence-option-weekly',
      // e.g. Weekly on Monday
      text: (
        <>
          <Trans id="recurrence-option-weekly">Weekly on</Trans>{' '}
          <DateFormat value={jsDate} format={{weekday: 'long'}} />
        </>
      ),
      isSelected: false,
    },
    {
      recurrence: buildRecurrence({
        freq: RecurrenceFrequenciesEnum.MONTHLY,
        byweekday: [{weekday: frontCalendarWeekday, n: weekOfMonth}],
      }),
      key: 'recurrence-option-monthly',
      // e.g. Monthly on third Monday, Monthly on last Friday
      text: (
        <>
          <Trans id="recurrence-option-monthly">Monthly on</Trans> {weekOfMonthLong}{' '}
          <DateFormat value={jsDate} format={{weekday: 'long'}} />
        </>
      ),
      isSelected: false,
    },
    {
      recurrence: buildRecurrence({freq: RecurrenceFrequenciesEnum.YEARLY}),
      key: 'recurrence-option-yearly',
      // e.g. Yearly on April 10
      text: (
        <>
          <Trans id="recurrence-option-yearly">Yearly on</Trans>{' '}
          <DateFormat value={jsDate} format={{month: 'long', day: 'numeric'}} />
        </>
      ),
      isSelected: false,
    },
  ];
}

function renderCustomRecurrenceDialog(
  setCustomRecurrenceDialog: (val: boolean) => void,
  props: RecurrenceDropdownBoxProps,
  selectedRecurrenceOption?: RecurrenceOption,
) {
  const onChange = props.onChange || noop;
  const selectedRecurrence = selectedRecurrenceOption && selectedRecurrenceOption.recurrence;

  return (
    <CustomRecurrenceDialog
      startDateTime={props.startDateTime}
      weekStart={props.weekStart}
      onSaveClick={(recurrenceModel: RecurrenceModel) => {
        setCustomRecurrenceDialog(false);
        onChange(recurrenceModel);
      }}
      onCancelClick={() => setCustomRecurrenceDialog(false)}
      selectedRecurrence={selectedRecurrence}
    />
  );
}
